import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { H2, HeroH1, P, SeoPageShell } from "./seo/SeoPageLayout";

type Item = { to: string; title: string; desc: string };

const PATTERN_GUIDES: Item[] = [
  {
    to: "/why-do-i-feel-exhausted-after-conversations",
    title: "Why do I feel exhausted after conversations",
    desc: "Why some conversations leave you drained when nothing obvious happened.",
  },
  {
    to: "/darvo-examples",
    title: "DARVO examples",
    desc: "Deny, Attack, Reverse Victim and Offender — the blame-flip pattern, in text.",
  },
  {
    to: "/reactive-abuse-examples",
    title: "Reactive abuse examples",
    desc: "When you finally react and get blamed for the reaction.",
  },
  {
    to: "/why-do-i-look-like-the-crazy-one",
    title: "Why do I look like the crazy one",
    desc: "Why the person who finally reacts ends up looking like the problem.",
  },
  {
    to: "/why-do-they-disappear-and-come-back",
    title: "Why do they disappear and come back",
    desc: "Why they go quiet and return right when you start to move on.",
  },
  {
    to: "/why-do-i-feel-like-im-walking-on-eggshells",
    title: "Why do I feel like I'm walking on eggshells",
    desc: "Why you brace before you speak, and why the calm never feels like rest.",
  },
  {
    to: "/emotional-invalidation-examples",
    title: "Emotional invalidation examples",
    desc: "The phrases that quietly erase your reality.",
  },
  {
    to: "/grey-rock-method-examples",
    title: "Grey rock method examples",
    desc: "How the grey rock method works, with real examples.",
  },
];

const TOOLS: Item[] = [
  {
    to: "/free-gaslighting-detector",
    title: "Free gaslighting detector",
    desc: "Recognize gaslighting language in texts and DMs, with examples and three free pattern previews — no signup.",
  },
  {
    to: "/spot-the-pattern",
    title: "Spot the Pattern — Daily Gaslighting Quiz",
    desc: "A 60-second daily challenge. Read the message, tap the phrase doing the work, name the pattern.",
  },
  {
    to: "/manipulative-text-message-analysis",
    title: "Manipulative text message analysis",
    desc: "How to read the pattern across a whole conversation, not just one line.",
  },
  {
    to: "/ai-detect-narcissistic-abuse",
    title: "AI detection of narcissistic abuse",
    desc: "How AI detects manipulation patterns in text.",
  },
  {
    to: "/chatgpt-vs-greyrock-ai",
    title: "ChatGPT vs GreyRock AI",
    desc: "What a general-purpose chat can and can't do with a manipulative message, and how a dedicated analysis tool differs.",
  },
];

const LINGUISTIC_ANALYZERS: Item[] = [
  {
    to: "/analysis/criticism-patterns-in-text-messages",
    title: "Criticism patterns in text messages",
    desc: "Fair feedback vs. character-attack criticism — the difference is scope, not vocabulary.",
  },
  {
    to: "/analysis/invalidation-patterns-in-texts",
    title: "Invalidation patterns in texts",
    desc: "How the topic quietly shifts from what happened to whether your reaction was 'reasonable.'",
  },
  {
    to: "/analysis/darvo-in-text-messages",
    title: "DARVO in text messages",
    desc: "Deny, Attack, Reverse — the three-part swap that can happen in a single sentence.",
  },
];

const Row = ({ item }: { item: Item }) => (
  <li className="border-t border-white/[0.06] py-5 first:border-t-0 first:pt-0">
    <Link
      to={item.to}
      className="block group"
    >
      <div className="text-[17px] md:text-[19px] font-semibold tracking-tight text-cyan-300/90 group-hover:text-cyan-200 underline-offset-4 group-hover:underline transition-colors">
        {item.title} →
      </div>
      <div className="mt-1.5 text-[15px] md:text-[16px] leading-[1.6] text-white/65">
        {item.desc}
      </div>
    </Link>
  </li>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mb-4 mt-14">
    {children}
  </p>
);

const Resources = () => {
  return (
    <SeoPageShell>
      <SEO
        title="Resources: Understanding Manipulation Patterns in Difficult Conversations"
        description="Essays and pattern guides on recognizing manipulation, gaslighting, DARVO, reactive abuse, and emotional invalidation in everyday conversations."
        path="/resources"
      />

      <HeroH1>Resources</HeroH1>
      <P>
        A growing library on recognizing the patterns behind difficult conversations — the
        mechanics of manipulation, the language for naming it, and the research shaping how
        these patterns are understood.
      </P>

      {/* Section 1 — featured essay */}
      <SectionLabel>Category essay</SectionLabel>
      <ul className="list-none pl-0">
        <Row
          item={{
            to: "/articulation-before-adjudication",
            title: "Articulation Before Adjudication",
            desc: "The emerging category of AI tools that help people understand difficult conversations — and the research behind it.",
          }}
        />
        <Row
          item={{
            to: "/overthinking-vs-pattern-recognition",
            title: "The Difference Between Overthinking and Pattern Recognition",
            desc: "Why some conversations replay in your mind for days — and why the loop ends with a pattern, not more facts.",
          }}
        />
      </ul>



      {/* Section 2 — pattern guides */}
      <SectionLabel>Pattern guides</SectionLabel>
      <ul className="list-none pl-0">
        {PATTERN_GUIDES.map((i) => (
          <Row key={i.to} item={i} />
        ))}
      </ul>

      {/* Section 3 — tools & analysis */}
      <SectionLabel>Tools &amp; analysis</SectionLabel>
      <ul className="list-none pl-0">
        {TOOLS.map((i) => (
          <Row key={i.to} item={i} />
        ))}
      </ul>

      {/* Section 4 — linguistic pattern analyzers */}
      <SectionLabel>Linguistic pattern analyzers</SectionLabel>
      <ul className="list-none pl-0">
        {LINGUISTIC_ANALYZERS.map((i) => (
          <Row key={i.to} item={i} />
        ))}
      </ul>
    </SeoPageShell>
  );
};

export default Resources;
